"use client";

import { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  Truck,
  Package,
  Sparkles,
  FileText,
  Mail,
  Moon,
  Building2,
  Factory,
  ShoppingBag,
  Hammer,
  Cog,
  Wrench,
  CalendarDays,
  ShieldCheck,
  MapPin,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "@/i18n/routing";
import type { Locale } from "@/i18n/routing";
import { BookingLink } from "@/components/ui/booking-link";
import { company } from "@/content/global";
import { PhoneCopyLink, isFinePointer } from "./phone-copy-link";
import { t as tLocalized, type Localized } from "@/lib/content";
import { cn } from "@/lib/cn";

const HUB_HREF: Record<string, string> = {
  leistungen: "/leistungen",
  branchen: "/branchen",
  unternehmen: "/ueber-isg",
};

/**
 * Mega-menu structure. All copy lives in the `MegaMenu` next-intl namespace
 * (edited in Sanity → UI-Texte); this array only holds the stable shape:
 * panel/link keys, hrefs and icons. Text is looked up via `t(key)`.
 */
type LinkItem = {
  /** Translation key stem — `t(\`${key}Label\`)` / `t(\`${key}Hint\`)`. */
  key: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
  /**
   * When set, render these literals instead of looking up the next-intl key.
   * Use for routes whose copy isn't yet in the Sanity `uiStrings` singleton.
   * Once migrated to Sanity, drop the field and add the key in Studio.
   */
  label?: Localized;
  hint?: Localized;
};

type Panel = {
  key: string;
  /** Translation key for the panel trigger label. */
  labelKey: string;
  links: LinkItem[];
  /** When set: translation key stems for the side CTA. */
  cta?: { titleKey: string; bodyKey: string; labelKey: string; href: string };
};

export const PANELS: Panel[] = [
  {
    key: "leistungen",
    labelKey: "panelLeistungen",
    links: [
      { key: "eiltransport", href: "/eiltransport-kurier", icon: Truck },
      {
        key: "nachtsprung",
        href: "/nachtsprung",
        icon: Moon,
        label: {
          de: "Nachtsprung",
          en: "Overnight",
          fr: "Transport de nuit",
          nl: "Nachttransport",
          it: "Trasporto notturno",
          pl: "Transport nocny",
          es: "Transporte nocturno",
          cs: "Noční přeprava",
          zh: "夜间直送",
          da: "Nattetransport",
        },
        hint: {
          de: "Abends abholen, morgens zustellen.",
          en: "Picked up tonight, delivered by morning.",
          fr: "Enlevé le soir, livré le matin.",
          nl: "'s Avonds opgehaald, 's ochtends bezorgd.",
          it: "Ritirato la sera, consegnato al mattino.",
          pl: "Odbiór wieczorem, dostawa rano.",
          es: "Recogida por la tarde, entrega por la mañana.",
          cs: "Vyzvednutí večer, doručení ráno.",
          zh: "傍晚取件，次日早晨送达。",
          da: "Hentet om aftenen, leveret om morgenen.",
        },
      },
      { key: "lkw", href: "/lkw-spedition", icon: Package },
      { key: "zusatzservices", href: "/zusatzservices", icon: Sparkles },
      { key: "dokZustellung", href: "/dokumentierte-zustellung", icon: FileText },
      { key: "kuendigung", href: "/kuendigungszustellung", icon: Mail },
    ],
    cta: {
      titleKey: "ctaLeistungenTitle",
      bodyKey: "ctaLeistungenBody",
      labelKey: "ctaLeistungenLabel",
      href: "/angebot",
    },
  },
  {
    key: "branchen",
    labelKey: "panelBranchen",
    links: [
      { key: "uebersicht", href: "/branchen", icon: Building2 },
      { key: "automotive", href: "/branchen/automotive", icon: Cog },
      { key: "maschinenbau", href: "/branchen/maschinenbau", icon: Wrench },
      { key: "handel", href: "/branchen/handel-ecommerce", icon: ShoppingBag },
      { key: "messe", href: "/messetransport", icon: CalendarDays },
      { key: "baugewerbe", href: "/branchen/baugewerbe", icon: Hammer },
      { key: "anlagenbau", href: "/branchen/anlagenbau", icon: Factory },
    ],
    cta: {
      titleKey: "ctaBranchenTitle",
      bodyKey: "ctaBranchenBody",
      labelKey: "ctaBranchenLabel",
      href: "/kontakt",
    },
  },
  {
    key: "unternehmen",
    labelKey: "panelUnternehmen",
    links: [
      { key: "ueberIsg", href: "/ueber-isg", icon: Building2 },
      { key: "standorte", href: "/standorte", icon: MapPin },
      { key: "team", href: "/team", icon: Cog },
      { key: "bewertungen", href: "/bewertungen", icon: Sparkles },
      { key: "presse", href: "/presse", icon: FileText },
      { key: "versicherung", href: "/versicherung-qualitaet", icon: ShieldCheck },
    ],
  },
];

export function MegaMenu() {
  const t = useTranslations("MegaMenu");
  const locale = useLocale() as Locale;
  const [open, setOpen] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(null);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  function scheduleClose() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(null), 120);
  }
  function cancelClose() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }
  // Telefon-Links: mit Maus/Trackpad bleibt das Panel offen, damit die
  // Sprechblase „Nummer kopiert“ sichtbar ist (schließt beim Verlassen).
  function closeUnlessCopying() {
    if (!isFinePointer()) setOpen(null);
  }

  return (
    <div
      ref={wrapRef}
      className="hidden lg:block"
      onMouseLeave={scheduleClose}
      onMouseEnter={cancelClose}
    >
      <nav aria-label={t("mainNav")} className="flex items-center gap-0.5">
        {PANELS.map((p) => {
          const isOpen = open === p.key;
          return (
            <button
              key={p.key}
              type="button"
              aria-expanded={isOpen}
              aria-haspopup="menu"
              onMouseEnter={() => {
                cancelClose();
                setOpen(p.key);
              }}
              onClick={() => setOpen(isOpen ? null : p.key)}
              className={cn(
                "inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-[13.5px] font-medium transition-colors",
                isOpen
                  ? "bg-soft text-ink-900"
                  : "text-ink-700 hover:bg-soft hover:text-ink-900",
              )}
            >
              {t(p.labelKey)}
              <ChevronDown
                className={cn(
                  "h-3 w-3 text-ink-500/70 transition-transform",
                  isOpen && "rotate-180",
                )}
                aria-hidden
              />
            </button>
          );
        })}
        <Link
          href="/kontakt"
          className="inline-flex items-center rounded-md px-3 py-1.5 text-[13.5px] font-medium text-ink-700 transition-colors hover:bg-soft hover:text-ink-900"
        >
          {t("contact")}
        </Link>
        <Link
          href="/transportpartner"
          className="inline-flex items-center rounded-md px-3 py-1.5 text-[13.5px] font-medium text-brand-700 transition-colors hover:bg-brand-50"
        >
          {tLocalized({ de: "Unternehmer werden", en: "Become a partner" }, locale) as string}
        </Link>
      </nav>

      {/* Panel — single positioned dropdown rendered for the active panel */}
      {open
        ? (() => {
            const panel = PANELS.find((p) => p.key === open);
            if (!panel) return null;
            return (
              <>
                <div
                  aria-hidden
                  onMouseEnter={scheduleClose}
                  className="fixed inset-x-0 top-[var(--header-h,7rem)] z-30 h-screen bg-ink-900/15 backdrop-blur-[2px] animate-[fadeIn_180ms_ease-out_both]"
                />
                <div
                  role="menu"
                  onMouseEnter={cancelClose}
                  onMouseLeave={scheduleClose}
                  className="fixed inset-x-0 top-[var(--header-h,7rem)] z-40 border-b border-line-200 bg-canvas shadow-[0_24px_48px_-16px_rgba(11,18,32,0.16),0_2px_4px_rgba(11,18,32,0.04)] animate-[fadeIn_220ms_ease-out_both]"
                >
                  <div className="container py-10 lg:py-12">
                    <div
                      className={cn(
                        "grid gap-10 lg:gap-14",
                        panel.cta ? "lg:grid-cols-[1fr_340px]" : "",
                      )}
                    >
                      <div>
                        <ul
                          className={cn(
                            "grid gap-x-8 gap-y-1",
                            panel.links.length > 4 ? "sm:grid-cols-2" : "",
                          )}
                        >
                          {panel.links.map((l) => {
                            const Icon = l.icon;
                            return (
                              <li key={l.href}>
                                <Link
                                  href={l.href}
                                  onClick={() => setOpen(null)}
                                  className="group flex items-start gap-3 rounded-lg px-3 py-3 transition-colors duration-150 hover:bg-soft/70"
                                >
                                  {Icon ? (
                                    <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-soft/60 text-ink-700 ring-1 ring-line-200/70 transition-colors duration-150 group-hover:bg-canvas group-hover:text-brand-600">
                                      <Icon className="h-4 w-4" />
                                    </span>
                                  ) : null}
                                  <span className="min-w-0">
                                    <span className="block font-display text-[14.5px] font-semibold text-ink-900">
                                      {l.label
                                        ? (tLocalized(l.label, locale) as string)
                                        : t(`${l.key}Label`)}
                                    </span>
                                    <span className="mt-1 block text-[12.5px] leading-snug text-ink-500">
                                      {l.hint
                                        ? (tLocalized(l.hint, locale) as string)
                                        : t(`${l.key}Hint`)}
                                    </span>
                                  </span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>

                      {panel.cta ? (
                        <aside className="rounded-2xl border border-line-200 bg-canvas p-6">
                          <p className="font-display text-[15px] font-semibold tracking-[-0.005em] text-ink-900">
                            {t(panel.cta.titleKey)}
                          </p>
                          <p className="mt-2 text-[13px] leading-relaxed text-ink-500">
                            {t(panel.cta.bodyKey)}
                          </p>
                          {panel.cta.href.startsWith("tel:") ? (
                            <PhoneCopyLink
                              href={panel.cta.href}
                              number={panel.cta.href === company.hotlineHref ? company.hotline : panel.cta.href.slice(4)}
                              onClick={closeUnlessCopying}
                              wrapperClassName="mt-5"
                              className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-ink-900 transition-colors hover:text-brand-600 tabnums"
                            >
                              {t(panel.cta.labelKey)}
                              <span aria-hidden className="transition-transform group-hover:translate-x-0.5">→</span>
                            </PhoneCopyLink>
                          ) : (
                            <BookingLink
                              href={panel.cta.href}
                              onClick={() => setOpen(null)}
                              className="group mt-5 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-ink-900 transition-colors hover:text-brand-600"
                            >
                              {t(panel.cta.labelKey)}
                              <span aria-hidden className="transition-transform group-hover:translate-x-0.5">→</span>
                            </BookingLink>
                          )}
                        </aside>
                      ) : null}
                    </div>

                    <div className="mt-10 flex items-center justify-between border-t border-line-200 pt-5">
                      <Link
                        href={HUB_HREF[panel.key] ?? "/"}
                        onClick={() => setOpen(null)}
                        className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-ink-900 transition-colors hover:text-brand-700"
                      >
                        {t("viewAll")}
                        <span aria-hidden>→</span>
                      </Link>
                      {panel.cta ? (
                        <PhoneCopyLink
                          href={company.hotlineHref}
                          number={company.hotline}
                          onClick={closeUnlessCopying}
                          wrapperClassName="hidden md:inline-flex"
                          className="inline-flex items-center gap-1.5 text-[13px] text-ink-500 transition-colors hover:text-ink-900 tabnums"
                        >
                          {t("preferPhone")} {company.hotline}
                        </PhoneCopyLink>
                      ) : null}
                    </div>
                  </div>
                </div>
              </>
            );
          })()
        : null}
    </div>
  );
}
